import { tryParseJSON } from '.';
import { COUPON_TYPE, DISCOUNT_TYPE, LOCAL_COUPON_VOUCHERS } from '../constants';
import { VOUCHER_CART_TYPE } from '../constants/voucher';

// Synchronize code between client and server
export const standardizeCouponVoucher = (couponVoucher) => {
     const couponCartType =
          couponVoucher.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_CART
               ? VOUCHER_CART_TYPE.DISCOUNT_ON_CART
               : couponVoucher.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_TOTAL
               ? VOUCHER_CART_TYPE.DISCOUNT_ON_PRODUCT
               : null;

     const standardizedCouponVoucher = {
          code: couponVoucher.code,
          category: couponVoucher.category,
          cartType: couponCartType || couponVoucher.cartType || VOUCHER_CART_TYPE.DISCOUNT_ON_CART,
          discountType: couponVoucher.discountType,
          discountValue: couponVoucher.discount || couponVoucher.discountValue || 0,
          validProductIds: couponVoucher.validProductIds || [],
     };

     return standardizedCouponVoucher;
};

// Synchronize code between client and server
export const standardizeCouponVouchers = (couponVouchers) => {
     const existedCodes = [];
     const standardizedCouponVouchers = [];

     couponVouchers.forEach((couponVoucher) => {
          const isExistCode = existedCodes.some((code) => code === couponVoucher.code);
          if (isExistCode) return;

          const standardizedItem = standardizeCouponVoucher(couponVoucher);

          standardizedCouponVouchers.push(standardizedItem);
          existedCodes.push(standardizedItem.code);
     });

     return standardizedCouponVouchers;
};

export const formatOriginalVoucherDiscount = (voucher) => {
     if (!voucher) return '';

     const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
     });

     const isPercent = voucher?.discountType === DISCOUNT_TYPE.PERCENT;

     const voucherDiscountValue = isPercent ? `${voucher?.discountValue}%` : formatter.format(voucher?.discountValue);

     return voucherDiscountValue;
};

// Clone from server calculateCartDiscountForApplyCoupon
export const calculateCodeDiscountOnCart = (payload) => {
     const { couponVoucher, cartItems } = payload;

     let cartDiscount = 0;

     const invalidVoucher = !couponVoucher || !Object.keys(couponVoucher).length || !couponVoucher.discountValue;
     const invalidCartItems = !Array.isArray(cartItems) || !cartItems.length;

     if (invalidVoucher || invalidCartItems) return cartDiscount;

     const newCartItems = calculateDiscountPriceWithCartItems(cartItems);

     const validProductIds = couponVoucher.validProductIds || [];
     const voucherDiscount = Number(couponVoucher.discountValue) || 0;

     // For vouchers, only the cart type is applicable on the cart
     if (couponVoucher.category === 'voucher') {
          couponVoucher.cartType = VOUCHER_CART_TYPE.DISCOUNT_ON_CART;
     }

     switch (couponVoucher.cartType) {
          case VOUCHER_CART_TYPE.DISCOUNT_ON_CART:
               cartDiscount = calculateCartDiscountWithDiscountOnCart({
                    cartItems: newCartItems,
                    discountType: couponVoucher.discountType,
                    discountValue: voucherDiscount,
                    validProductIds,
               });
               break;

          case VOUCHER_CART_TYPE.DISCOUNT_ON_PRODUCT:
               cartDiscount = calculateCartDiscountWithDiscountOnProduct({
                    cartItems: newCartItems,
                    discountType: couponVoucher.discountType,
                    discountValue: voucherDiscount,
                    validProductIds,
               });
               break;

          default:
               break;
     }

     return cartDiscount;
};

export const calculateDiscountPriceWithCartItems = (cartItems) => {
     if (!cartItems?.length) return [];

     const newCartItems = cartItems.map((cartItem) => {
          const cartItemQuantity = Number(cartItem.quantity) || 1;
          const subTotal = cartItem.price * cartItemQuantity;
          let itemDiscount = 0;

          if (cartItem.discount && cartItem.discount > 0) {
               itemDiscount =
                    cartItem.discountType === DISCOUNT_TYPE.PRICE
                         ? cartItem.discount * cartItemQuantity
                         : ((cartItem.price * cartItem.discount) / 100) * cartItemQuantity;
          }

          if (itemDiscount > subTotal) itemDiscount = subTotal;

          const total = Math.max(subTotal - itemDiscount, 0) || 0;

          return {
               id: cartItem.id,
               productId: cartItem.productId,
               name: cartItem.name,
               price: cartItem.price,
               quantity: cartItemQuantity,
               discount: cartItem.discount || 0,
               discountType: cartItem.discountType,
               total,
          };
     });

     return newCartItems;
};

export const calculateCartDiscountWithDiscountOnCart = (payload) => {
     const { cartItems, discountType, discountValue, validProductIds } = payload;
     let cartDiscount = 0;

     if (discountType === DISCOUNT_TYPE.PRICE) {
          cartDiscount = cartItems.reduce((cartItemAcc, currCartItem) => {
               if (!validProductIds.length || validProductIds.includes(currCartItem.productId)) {
                    const itemCouponDiscount = Math.min(discountValue, currCartItem.total) || 0;

                    return cartItemAcc + itemCouponDiscount;
               }

               return cartItemAcc;
          }, 0);

          if (cartDiscount > discountValue) cartDiscount = discountValue;
     } else {
          const cartDiscountTotal =
               cartItems.reduce((cartItemAcc, currCartItem) => {
                    if (!validProductIds.length || validProductIds.includes(currCartItem.productId)) {
                         return cartItemAcc + currCartItem.total;
                    }

                    return cartItemAcc;
               }, 0) || 1;

          cartDiscount = (cartDiscountTotal * discountValue) / 100;
     }

     return cartDiscount;
};

export const calculateCartDiscountWithDiscountOnProduct = (payload) => {
     const { cartItems, discountType, discountValue, validProductIds } = payload;
     let cartDiscount = 0;

     if (discountType === DISCOUNT_TYPE.PRICE) {
          cartDiscount = cartItems.reduce((cartItemAcc, currCartItem) => {
               if (!validProductIds.length || validProductIds.includes(currCartItem.productId)) {
                    let itemCouponDiscount = discountValue * currCartItem.quantity;

                    if (itemCouponDiscount > currCartItem.total) {
                         itemCouponDiscount = currCartItem.total;
                    }

                    return cartItemAcc + itemCouponDiscount;
               }

               return cartItemAcc;
          }, 0);
     } else {
          const cartDiscountTotal =
               cartItems.reduce((cartItemAcc, currCartItem) => {
                    if (!validProductIds.length || validProductIds.includes(currCartItem.productId)) {
                         return cartItemAcc + currCartItem.total;
                    }

                    return cartItemAcc;
               }, 0) || 1;

          cartDiscount = (cartDiscountTotal * discountValue) / 100;
     }

     return cartDiscount;
};

// Clone from server checkVouchersDiscountByCart
export const calculateTotalVoucherDiscountOnCart = (payload) => {
     const { vouchers, orderItems } = payload;
     let vouchersDiscount = 0;

     const cartItems = orderItems.map((orderItem) => ({
          id: orderItem.id,
          productId: orderItem.productId,
          eventId: orderItem.eventId || '',
          sku: orderItem.sku,
          name: orderItem.name,
          slug: orderItem.slug,
          price: orderItem.price,
          quantity: Number(orderItem.quantity) || 1,
          discount: Number(orderItem.discount) || 0,
          discountType: orderItem.discountType || DISCOUNT_TYPE.PRICE,
          tierId: orderItem.tierId,
          isCredit: orderItem.isCredit,
     }));

     for (let i = 0; i < vouchers.length; i++) {
          const voucher = vouchers[i];

          // if (voucher.cartType !== VOUCHER_CART_TYPE.DISCOUNT_ON_CART) continue;

          const _vouchersDiscount = calculateCodeDiscountOnCart({ couponVoucher: voucher, cartItems });
          vouchersDiscount += _vouchersDiscount;
     }

     return vouchersDiscount;
};

// Check valid local coupon vouchers
export const findLocalInvalidCodes = (cartItems) => {
     const couponVouchers = tryParseJSON(localStorage.getItem(LOCAL_COUPON_VOUCHERS));
     if (!couponVouchers?.length) return [];

     const invalidCodes = [];

     for (let i = 0; i < couponVouchers.length; i++) {
          const voucher = couponVouchers[i];

          const validProductIds =
               voucher.validProductIds?.filter((productId) => cartItems.some((cartItem) => cartItem.productId === productId)) ?? [];

          if (voucher.validProductIds?.length && !validProductIds.length) {
               invalidCodes.push(voucher.code);
          }
     }

     return invalidCodes;
};

// Alternated Description For Voucher
export const createCustomVoucherDescription = (voucher, products) => {
     const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
     });

     const voucherRuleDiscountValue =
          voucher?.ruleDiscountType === DISCOUNT_TYPE.PERCENT ? `${voucher?.ruleDiscountValue}%` : formatter.format(voucher?.ruleDiscountValue);

     const voucherDiscountValue =
          voucher?.discountType === DISCOUNT_TYPE.PERCENT ? `${voucher?.discountValue}%` : formatter.format(voucher?.discountValue);

     const discountOnProductsMsg = () => {
          const isDiscountOnProducts = !!voucher.limitOnProducts?.length;
          if (!isDiscountOnProducts) return '';

          const voucherRuleProductPublishNames = [];
          products?.forEach((product) => {
               const productExists = voucher.limitOnProducts.includes(product.id);
               if (productExists) voucherRuleProductPublishNames.push(product.publishName);
          });

          const validProducts = products.filter((product) => product.price);

          const isIncludedAll = validProducts.length === voucherRuleProductPublishNames.length;
          const isIncludedOne = voucherRuleProductPublishNames.length === 1;

          const productsMsg = isIncludedAll
               ? `any products`
               : isIncludedOne
               ? `for the ${voucherRuleProductPublishNames[0]}`
               : `for one of the ${voucherRuleProductPublishNames.join(', ')}`;

          return productsMsg;
     };

     const alternatedDescription = `Save ${voucherDiscountValue || voucherRuleDiscountValue} ${discountOnProductsMsg()} on this order.`;

     return alternatedDescription;
};

export const createAppliedVoucherDescriptionTooltip = (couponVouchers, cartItems) => {
     // Discount on Product: Save $20 on your purchase of the Difficult Patients Online Video Course.
     // Discount on cart: Save $20 on any product cart purchase.

     const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
     });

     const appliedDescriptions = [];
     for (let i = 0; i < couponVouchers.length; i++) {
          const couponVoucher = couponVouchers[i];

          const voucherDiscountValue =
               couponVoucher?.discountType === DISCOUNT_TYPE.PERCENT
                    ? `${couponVoucher?.discountValue}%`
                    : formatter.format(couponVoucher?.discountValue);

          const discountObjectMsg = () => {
               const validProductIds = couponVoucher.validProductIds || [];

               const isDiscountOnProducts = !!validProductIds.length;
               if (!isDiscountOnProducts) return 'any product cart purchase';

               const representativeProductName = cartItems
                    .sort((a, b) => b.price - a.price)
                    .find((item) => validProductIds.includes(item.productId))?.name;

               const productsMsg = `${representativeProductName}`;

               return 'your purchase of each ' + productsMsg;
          };

          const appliedDescription = `Save ${voucherDiscountValue} on ${discountObjectMsg()}.`;

          appliedDescriptions.push(appliedDescription);
     }

     const appliedDescriptionString = appliedDescriptions.join('<br>');

     return appliedDescriptionString;
};

export const validateAvailableVouchers = (vouchers, cartItems = []) => {
     const itemProductIds = cartItems.reduce((acc, item) => {
          acc[item.productId] = item.name;
          return acc;
     }, {});

     const validVouchers = [];

     for (let i = 0; i < vouchers.length; i++) {
          const availableVoucher = vouchers[i];

          if (!availableVoucher.limitOnProducts.length) {
               validVouchers.push(availableVoucher);
               continue;
          }

          const isIncludeItem = availableVoucher.limitOnProducts.some((limitProductId) => !!itemProductIds[limitProductId]);
          if (!isIncludeItem) continue;

          validVouchers.push(availableVoucher);
     }

     return validVouchers;
};
